import {Card, FormControl, FormField, Separator} from 'platform/components';
import {Grid, GridItem, Show, Space, VStack} from 'platform/foundation';

import {FieldPathByValue, FieldPathValue, Path, UseFormReturn, useWatch} from 'react-hook-form';

import {useGetSeriesListQuery} from '@dms/api/core';
import i18n from '@dms/i18n';
import {useCashRegister, useDocumentTemplates} from '@dms/shared';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {FormType} from '../../../types/ServiceOrderVariant';
import {getPaymentCardPrefill} from '../../../utils/getPaymentCardPrefill';
import {getPrimaryDefaultTemplateId} from '../../../utils/getPrimaryDefaultTemplateId';
import {getSoleSeriesId} from '../../../utils/getSoleSeriesId';

interface PaymentTypeProps extends RequiredTestIdProps {
  formApi: UseFormReturn<FormType>;
  control: FormControl<FormType>;
  formSection: Path<FormType>;
  title: string;
  isInitiatePaymentVisible?: boolean;
  isCashRegisterVisible?: boolean;
}

// Every document of a payment card is issued in the invoice series, so these follow it.
const DEPENDENT_DOC_SERIES_SECTIONS = [
  'deposit',
  'invoiceCorrection',
  'invoiceEu',
  'invoiceEuCorrection',
  'invoiceOutsideEu',
  'invoiceOutsideEuCorrection',
  'taxDocument',
  'taxDocumentCorrection',
] satisfies (keyof FormType['paymentType']['cardPayment'])[];

// `getValues` returns the union of every field, so the id is narrowed out rather than asserted.
const toStringOrUndefined = (value: FieldPathValue<FormType, Path<FormType>>) =>
  typeof value === 'string' ? value : undefined;

export function PaymentType(props: PaymentTypeProps) {
  const isAllowed = useWatch({
    control: props.control,
    name: `${props.formSection}.isAllowed` as Path<FormType>,
  }) as boolean;
  const isAutoInitiatePaymentChecked = useWatch({
    control: props.control,
    name: `${props.formSection}.isAutoInitiatePayment` as Path<FormType>,
  }) as boolean;

  const {options, isLoading, soleDocSeriesId} = useGetSeriesListQuery(
    {
      type: ['accounting/invoice'],
    },
    {
      selectFromResult: (result) => ({
        ...result,
        options:
          result?.data?.map((serie) => ({
            label: serie.name,
            value: serie.id,
          })) ?? [],
        soleDocSeriesId: getSoleSeriesId(result?.data, 'accounting/invoice'),
      }),
    }
  );

  const {cashRegisterOptions, isLoading: isCashRegisterLoading} = useCashRegister();

  const [getTemplates, {isLoading: isTemplatesLoading}] = useDocumentTemplates();
  const {options: invoiceTemplateOptions, defaultTemplates} = getTemplates('workshop-invoice');

  const primaryTemplateId = getPrimaryDefaultTemplateId(defaultTemplates);

  const getDocSeriesId = (section: string) =>
    toStringOrUndefined(
      props.formApi.getValues(`${props.formSection}.${section}.docSeriesId` as Path<FormType>)
    );

  const setDependentDocSeriesIds = (value: string | null) => {
    DEPENDENT_DOC_SERIES_SECTIONS.forEach((section) =>
      props.formApi.setValue(
        `${props.formSection}.${section}.docSeriesId` as Path<FormType>,
        value,
        {shouldValidate: true}
      )
    );
  };

  // Writes must validate: plain `setValue` does not revalidate under `mode="onChange"`, so the
  // card would keep the "required" errors it was just filled to resolve.
  const prefillCard = () => {
    const prefill = getPaymentCardPrefill({
      invoiceTemplateId: toStringOrUndefined(
        props.formApi.getValues(`${props.formSection}.invoice.templateId` as Path<FormType>)
      ),
      invoiceDocSeriesId: getDocSeriesId('invoice'),
      dependentDocSeriesIds: Object.fromEntries(
        DEPENDENT_DOC_SERIES_SECTIONS.map((section) => [section, getDocSeriesId(section)])
      ),
      primaryTemplateId,
      soleDocSeriesId,
    });

    Object.entries(prefill).forEach(([path, value]) =>
      props.formApi.setValue(`${props.formSection}.${path}` as Path<FormType>, value, {
        shouldValidate: true,
      })
    );
  };

  return (
    <Card
      title={props.title}
      isExpanded={isAllowed}
      control={{
        type: 'switch',
        value: isAllowed,
        isDisabled: isLoading || isTemplatesLoading,
        onChange: (enabled) => {
          props.formApi.setValue(`${props.formSection}.isAllowed` as Path<FormType>, enabled);
          props.formApi.setValue('paymentType.defaultPaymentType', null);

          if (enabled) {
            prefillCard();
          }
        },
      }}
      variant="inlineGrey"
      data-testid={suffixTestId('card', props)}
    >
      <Separator spacing={0} />
      <Space vertical={4} />
      <VStack spacing={4}>
        <Grid columns={2} horizontalSpacing={6}>
          <GridItem>
            <FormField
              control={props.control}
              name={`${props.formSection}.invoice.templateId` as Path<FormType>}
              type="choice"
              label={i18n.t('entity.warehouse.labels.invoiceTemplate')}
              options={invoiceTemplateOptions ?? []}
              isLoading={isTemplatesLoading}
              isRequired
              isNotClearable
              data-testid={suffixTestId('invoiceTemplate', props)}
            />
          </GridItem>
        </Grid>
        <Grid columns={2} horizontalSpacing={6}>
          <FormField
            control={props.control}
            name={`${props.formSection}.invoice.docSeriesId` as Path<FormType>}
            type="choice"
            label={i18n.t('entity.warehouse.labels.invoiceDocumentSerie')}
            options={options}
            isLoading={isLoading}
            isRequired
            isNotClearable
            onChange={(value) => setDependentDocSeriesIds(typeof value === 'string' ? value : null)}
            data-testid={suffixTestId('invoiceDocumentSerie', props)}
          />
        </Grid>
        <Grid columns={2} horizontalSpacing={6}>
          <FormField
            control={props.control}
            name={`${props.formSection}.deposit.docSeriesId` as Path<FormType>}
            type="choice"
            label={i18n.t('entity.settings.labels.proformaInvoice')}
            options={options}
            isLoading={isLoading}
            isRequired
            isNotClearable
            data-testid={suffixTestId('proformaInvoice', props)}
          />
          <FormField
            control={props.control}
            name={`${props.formSection}.invoiceCorrection.docSeriesId` as Path<FormType>}
            type="choice"
            label={i18n.t('entity.settings.labels.correctiveDocument')}
            options={options}
            isLoading={isLoading}
            isRequired
            isNotClearable
            data-testid={suffixTestId('correctiveDocument', props)}
          />
        </Grid>
        <Grid columns={2} horizontalSpacing={6}>
          <FormField
            control={props.control}
            name={`${props.formSection}.taxDocument.docSeriesId` as Path<FormType>}
            type="choice"
            label={i18n.t('entity.settings.labels.taxDocument')}
            options={options}
            isLoading={isLoading}
            isRequired
            isNotClearable
            data-testid={suffixTestId('taxDocument', props)}
          />
          <FormField
            control={props.control}
            name={`${props.formSection}.taxDocumentCorrection.docSeriesId` as Path<FormType>}
            type="choice"
            label={i18n.t('entity.settings.labels.taxDocumentCorrection')}
            options={options}
            isLoading={isLoading}
            isRequired
            isNotClearable
            data-testid={suffixTestId('taxDocumentCorrection', props)}
          />
        </Grid>
        <Grid columns={2} horizontalSpacing={6}>
          <FormField
            control={props.control}
            name={`${props.formSection}.invoiceEu.docSeriesId` as Path<FormType>}
            type="choice"
            label={i18n.t('entity.settings.labels.invoiceEu')}
            options={options}
            isLoading={isLoading}
            isRequired
            isNotClearable
            data-testid={suffixTestId('invoiceEu', props)}
          />
          <FormField
            control={props.control}
            name={`${props.formSection}.invoiceEuCorrection.docSeriesId` as Path<FormType>}
            type="choice"
            label={i18n.t('entity.settings.labels.correctiveDocumentEu')}
            options={options}
            isLoading={isLoading}
            isRequired
            isNotClearable
            data-testid={suffixTestId('correctiveDocumentEu', props)}
          />
        </Grid>
        <Grid columns={2} horizontalSpacing={6}>
          <FormField
            control={props.control}
            name={`${props.formSection}.invoiceOutsideEu.docSeriesId` as Path<FormType>}
            type="choice"
            label={i18n.t('entity.settings.labels.invoiceOutsideEu')}
            options={options}
            isLoading={isLoading}
            isRequired
            isNotClearable
            data-testid={suffixTestId('invoiceOutsideEu', props)}
          />
          <FormField
            control={props.control}
            name={`${props.formSection}.invoiceOutsideEuCorrection.docSeriesId` as Path<FormType>}
            type="choice"
            label={i18n.t('entity.settings.labels.correctiveDocumentOutsideEu')}
            options={options}
            isLoading={isLoading}
            isRequired
            isNotClearable
            data-testid={suffixTestId('correctiveDocumentOutsideEu', props)}
          />
        </Grid>
        <Show when={props.isInitiatePaymentVisible || props.isCashRegisterVisible}>
          <Grid columns={2} horizontalSpacing={6}>
            <Show when={props.isInitiatePaymentVisible}>
              <FormField
                control={props.control}
                name={
                  `${props.formSection}.isAutoInitiatePayment` as FieldPathByValue<
                    FormType,
                    boolean | Nullish
                  >
                }
                type="checkbox"
                label={i18n.t('entity.settings.labels.initiatePaymentAutomatically')}
                helperText={i18n.t('entity.settings.labels.initiatePaymentAutomaticallyHelper')}
                data-testid={suffixTestId('initiatePaymentAutomatically', props)}
              />
            </Show>
            <Show when={props.isCashRegisterVisible && isAutoInitiatePaymentChecked}>
              <FormField
                control={props.control}
                name={`${props.formSection}.cashRegisterId` as Path<FormType>}
                type="choice"
                label={i18n.t('general.labels.cashRegister')}
                options={cashRegisterOptions}
                isLoading={isCashRegisterLoading}
                isRequired
                data-testid={suffixTestId('cashRegister', props)}
              />
            </Show>
          </Grid>
        </Show>
      </VStack>
    </Card>
  );
}
